import React, { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Loader2,
  Copy,
  BookMarked,
  Heart,
  RefreshCw
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const promptSuggestions = [
  "Hoy sentí paz al leer las escrituras sobre el perdón...",
  "Me pregunto cómo aplicar mejor la fe en mi vida diaria...",
  "Estoy enfrentando una decisión difícil y busco guía...",
  "Reflexiono sobre el significado del sacrificio de Cristo...",
  "Quiero entender mejor el papel del Espíritu Santo en mi vida...",
  "Me siento agradecido por las bendiciones del templo..."
];

const SpiritualConnection = () => {
  const { token } = useAuth();

  const [reflection, setReflection] = useState('');
  const [context, setContext] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reflection.trim()) {
      toast.error('Por favor escribe tu reflexión espiritual');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/spiritual-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          reflection: reflection,
          context: context || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data.response);
        toast.success('Conexión espiritual generada');
      } else {
        toast.error('Error al procesar tu reflexión');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(result);
      toast.success('Copiado al portapapeles');
    }
  };

  const saveAsNote = async () => {
    if (!noteTitle.trim()) {
      toast.error('Ingresa un título para la nota');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: noteTitle,
          content: `Mi reflexión:\n${reflection}\n\n---\n\nConexión Espiritual:\n${result}`,
          scripture_ref: null,
          tags: ['conexión espiritual', 'reflexión personal']
        })
      });

      if (response.ok) {
        toast.success('Reflexión guardada exitosamente');
        setSaveDialogOpen(false);
        setNoteTitle('');
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setReflection(suggestion);
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="spiritual-connection">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-primary" />
            Conexión Espiritual Profunda
          </h1>
          <p className="text-muted-foreground">
            Comparte tu reflexión y recibe guía espiritual con escrituras relacionadas
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Heart className="h-5 w-5 text-primary" />
                Tu Reflexión
              </CardTitle>
              <CardDescription>
                Escribe tus pensamientos, sentimientos o preguntas espirituales
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Reflexión Espiritual *</Label>
                  <Textarea
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    placeholder="Escribe tu reflexión, pregunta o sentimiento espiritual aquí..."
                    className="min-h-[180px] scripture-text"
                    data-testid="reflection-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contexto adicional (opcional)</Label>
                  <Input
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Ej: Estoy estudiando el Libro de Mormón, enfrentando una prueba..."
                    data-testid="context-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Sugerencias para comenzar:</Label>
                  <div className="flex flex-wrap gap-2">
                    {promptSuggestions.slice(0, 3).map((suggestion, i) => (
                      <Button
                        key={`suggestion-${i}`}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs h-auto py-1.5 px-2"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion.slice(0, 40)}...
                      </Button>
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="generate-connection-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generando conexión...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generar Conexión Espiritual
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Guía Espiritual</CardTitle>
              <CardDescription>
                Reflexiones ampliadas, escrituras y preguntas introspectivas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard}>
                      <Copy className="h-4 w-4 mr-1" />
                      Copiar
                    </Button>
                    <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <BookMarked className="h-4 w-4 mr-1" />
                          Guardar
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Guardar Reflexión</DialogTitle>
                          <DialogDescription>
                            Guarda esta conexión espiritual en tu repositorio
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>Título</Label>
                            <Input
                              value={noteTitle}
                              onChange={(e) => setNoteTitle(e.target.value)}
                              placeholder="Ej: Reflexión sobre la fe"
                              data-testid="spiritual-save-title"
                            />
                          </div>
                          <Button
                            onClick={saveAsNote}
                            disabled={saving}
                            className="w-full"
                            data-testid="spiritual-confirm-save"
                          >
                            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Guardar Reflexión
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  <ScrollArea className="h-[420px] pr-4">
                    <div className="prose prose-sm dark:prose-invert max-w-none markdown-content">
                      {result.split('\n').map((paragraph, i) => (
                        <p key={`result-p-${i}`}>{paragraph}</p>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[420px] text-muted-foreground">
                  <Sparkles className="h-16 w-16 mb-4 opacity-20" />
                  <p className="text-center max-w-xs">
                    Comparte tu reflexión espiritual y recibe una guía personalizada con escrituras relacionadas
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Inspirational Quote */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <blockquote className="text-center">
              <p className="scripture-text text-lg italic text-foreground/80">
                "Allegaos a mí, y yo me allegaré a vosotros; buscadme diligentemente, y me hallaréis; pedid, y recibiréis; llamad, y se os abrirá."
              </p>
              <footer className="text-primary font-medium mt-3">
                — Doctrina y Convenios 88:63
              </footer>
            </blockquote>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SpiritualConnection;
